/**
 * @file Checkout product helpers.
 */

import { AdditionalProductMeta, GoalRelatedData, Product, ProductMeta } from "../../types";

export const CHOSEN_PRODUCT_ID = "460964";
export const SPONSORSHIP_MONTHLY_PRODUCT_ID = "380";

const chosenProductDetails: Product = {
  name: "Chosen Sponsorship",
  product_id: CHOSEN_PRODUCT_ID,
  sku: "s1000002",
  item_brand: "chosen",
  id: "s1000002",
  price: 39,
  category: "sponsorship",
  quantity: 1,
  frequency: "monthly",
  meta: {
    productTitle: "Chosen Sponsorship",
    wvus_product_event_number: "",
    wvus_product_event_keyword: "",
    pa_uom: "mon",
    wvus_product_streamlined_product: "true"
  }
};

const sponsorshipProductDetails: Product = {
  name: "Child Sponsorship",
  product_id: SPONSORSHIP_MONTHLY_PRODUCT_ID,
  sku: "s1000001",
  item_brand: "sponsorship",
  id: "s1000001",
  price: 39,
  category: "sponsorship",
  quantity: 1,
  frequency: "monthly",
  meta: {
    productTitle: "Sponsorship",
    wvus_product_child_identifier: "",
    pa_uom: "mon",
    wvus_product_streamlined_product: "true"
  }
};

type GetCheckoutProductParams = {
  eventNumber?: string | number;
  keyword?: string;
  cobrand?: string;
  productType?: "sponsorship" | "chosen" | "singleGift" | "pledge";
  goalTrackerInfo?: {
    goalRef: string;
    relatedData: GoalRelatedData | null;
  };
  sponsorshipInfo?: {
    identifier: string;
    countryName?: string;
    projectName?: string;
    photoUrl?: string;
    childName?: string;
  };
};

/**
 * Retrieves the checkout product details based on the provided parameters.
 *
 * @param params - The parameters required to get the checkout product.
 * @param params.eventNumber - The event number associated with the product.
 * @param params.keyword - The keyword associated with the product.
 * @param params.cobrand - The cobrand information for the product.
 * @param params.goalTrackerInfo - The goal tracker information for the product.
 * @param params.productType - The type of the product (e.g., "sponsorship").
 * @param params.sponsorshipInfo - The sponsorship information for the product.
 *
 * @returns The product details including additional metadata.
 */
export const getCheckoutProduct = (params: GetCheckoutProductParams): Product => {
  const { eventNumber, keyword, cobrand, goalTrackerInfo, productType, sponsorshipInfo } = params;
  const additionalMetaData: AdditionalProductMeta = {};
  let productDetails: Product;

  if (productType === "sponsorship") {
    productDetails = sponsorshipProductDetails;
  } else {
    productDetails = chosenProductDetails;
  }

  if (eventNumber) {
    additionalMetaData.wvus_product_event_number = eventNumber;
  }

  if (keyword) {
    additionalMetaData.wvus_product_event_keyword = keyword;
  }

  if (cobrand) {
    additionalMetaData.wvus_product_event_cobrand = cobrand;
  }

  if (sponsorshipInfo) {
    additionalMetaData.wvus_product_child_identifier = sponsorshipInfo.identifier;
    additionalMetaData.wvus_product_countryname = sponsorshipInfo.countryName;
    additionalMetaData.wvus_product_image = sponsorshipInfo.photoUrl;
    additionalMetaData.wvus_product_name = sponsorshipInfo.childName;
    productDetails.countryName = sponsorshipInfo.countryName;
    productDetails.projectName = sponsorshipInfo.projectName;
  }

  if (goalTrackerInfo) {
    additionalMetaData.wvus_product_goal_ref = goalTrackerInfo.goalRef;
    if (goalTrackerInfo.relatedData) {
      additionalMetaData.wvus_product_goal_related_data = goalTrackerInfo.relatedData;
    }
  }

  return {
    ...productDetails,
    meta: {
      ...(productDetails.meta || {}),
      ...additionalMetaData
    } as ProductMeta & AdditionalProductMeta
  };
};
